import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

// Tracks whether the user has switched tabs at least once in this Tabs instance,
// so the entrance animation only plays on interaction (not on initial mount).
const TabsSwitchedContext = React.createContext(false)

const Tabs = React.forwardRef(({ onValueChange, ...props }, ref) => {
  const [hasSwitched, setHasSwitched] = React.useState(false)
  const handleValueChange = React.useCallback(
    (value) => {
      setHasSwitched(true)
      onValueChange?.(value)
    },
    [onValueChange]
  )
  return (
    <TabsSwitchedContext.Provider value={hasSwitched}>
      <TabsPrimitive.Root ref={ref} onValueChange={handleValueChange} {...props} />
    </TabsSwitchedContext.Provider>
  )
})
Tabs.displayName = "Tabs"

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    )}
    {...props} />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => {
  const hasSwitched = React.useContext(TabsSwitchedContext)
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        // Hide any inactive panel that may linger for a frame during the switch
        // (prevents the brief "both panels stacked" flash).
        "data-[state=inactive]:hidden",
        // Slide-in only after the first interaction, and only if the user
        // hasn't requested reduced motion.
        hasSwitched &&
          "data-[state=active]:motion-safe:animate-in data-[state=active]:motion-safe:slide-in-from-right-3 data-[state=active]:motion-safe:duration-300",
        className
      )}
      {...props} />
  )
})
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
